import { onCLS, onLCP, onFID, onINP } from 'web-vitals/attribution';
import { findDataComponent } from '../utils/performance-observer-helpers';

const getExperienceConfig = () => window.__EXPERIENCE_PROPS__.expConfiguration || {};

let pollCounter = 0;
const POLL_TIMEOUT = 3000;
const POLL_RETRIES = 3;
const CLS_DECIMALS = 3;

const addReportValue = ({ name, value }) => {
  if (typeof window !== 'undefined' && window.newrelic) {
    window.newrelic.setCustomAttribute(name, value);
  }
};

const getQuantumMetricId = () => {

  pollCounter += 1;

  const quantumMetricID = (
    typeof window.QuantumMetricAPI !== 'undefined'
    && typeof window.QuantumMetricAPI.getSessionID === 'function'
    && window.QuantumMetricAPI.getSessionID()
  ) || undefined;

  if (quantumMetricID) {
    window.newrelic.setCustomAttribute('quantumMetricId', quantumMetricID);
    return;
  }

  if (pollCounter > POLL_RETRIES) return;

  setTimeout(getQuantumMetricId, POLL_TIMEOUT);
};

let longTaskObserver;
let longTaskCount = 0;

const recordLongTasks = () => {
  longTaskObserver = new PerformanceObserver((list) => {
    const perfEntries = list.getEntries();
    longTaskCount += perfEntries.length;
  });
  longTaskObserver.observe({ entryTypes: ['longtask'] });
};

export const performanceObserver = () => {
  const perfObserverEnabled = getExperienceConfig().PERFORMANCE_OBSERVER_ENABLED === 'true';

  if (!perfObserverEnabled) return;

  onCLS((info) => {
    if (!info || !info.value) return;

    const { entries, attribution } = info;
    const { largestShiftTarget, largestShiftValue, largestShiftEntry } = attribution;

    addReportValue({ name: 'CLS-LargestShiftTarget', value: largestShiftTarget });
    addReportValue({ name: 'CLS-LargestShiftValue', value: largestShiftValue?.toFixed(CLS_DECIMALS) });

    const sourcesOuterHtml = [];
    const dataComponents = [];

    if (Array.isArray(entries)) {
      entries.forEach((entry) => {

        if (Array.isArray(entry.sources)) {
          entry.sources.forEach((source) => {
            if (source?.node) {
              const node = source.node;
              sourcesOuterHtml.push(node.outerHTML);

              const dataComponent = findDataComponent({ node });
              if (dataComponent && !dataComponents.includes(dataComponent)) {
                dataComponents.push(dataComponent);
              }
            }
          });
        }
      });

      addReportValue({ name: 'CLS-Components', value: dataComponents.join(', ') });
      addReportValue({ name: 'CLS-Elements', value: sourcesOuterHtml.filter((html) => html !== '').join(', ') });
    }

    if (!largestShiftEntry || !Array.isArray(largestShiftEntry.sources)) return;

    // eslint-disable-next-line no-restricted-syntax
    for (const { node } of largestShiftEntry.sources) {
      const dataComponent = findDataComponent({ node });
      if (dataComponent) {
        addReportValue({ name: 'CLS-LargestShift-Component', value: dataComponent });
        break;
      }
    }
  });

  onLCP((info) => {
    if (!info || !info.value) return;

    const { lcpEntry, timeToFirstByte } = info.attribution || {};
    const { url, element } = lcpEntry;

    addReportValue({ name: 'LCP-Url', value: url });
    addReportValue({ name: 'LCP-TTFB', value: timeToFirstByte });

    if (element) {
      const dataComponent = findDataComponent({ node: element });
      addReportValue({ name: 'LCP-Component', value: dataComponent });
      addReportValue({ name: 'LCP-Element', value: element.outerHTML });
    }
  });

  onFID((info) => {
    if (longTaskObserver) longTaskObserver.disconnect();
    addReportValue({ name: 'FID-Long-Task-Count', value: longTaskCount });

    if (!info || !info.value) return;

    const { eventEntry, eventTarget } = info.attribution || {};

    if (!eventEntry) return;

    addReportValue({ name: 'FID-TargetElement', value: eventTarget });

    const target = eventEntry.target;

    if (!target) return;

    const dataComponent = findDataComponent({ node: target });
    addReportValue({ name: 'FID-Component', value: dataComponent });
  });

  onINP((info) => {
    if (!info || !info.value) return;

    const { eventTarget, eventEntry } = info.attribution || {};

    if (!eventTarget) return;

    addReportValue({ name: 'INP-TargetElement', value: eventTarget });

    const target = eventEntry.target;

    if (!target) return;

    const dataComponent = findDataComponent({ node: target });
    addReportValue({ name: 'INP-Component', value: dataComponent });
  });

  recordLongTasks();
  setTimeout(getQuantumMetricId, POLL_TIMEOUT);
};